import { UpdateLocationRequest } from '@/api/locations/types';
import FormDialog from '@/components/ui/form-dialog';
import TextField from '@/components/ui/form-inputs/TextField';
import { Level, Location } from '@/core/entities';
import useToastMessage from '@/shared/context/toast-message-context';
import { capitalize } from '@mui/material';
import { FunctionComponent, ReactElement, useMemo } from 'react';
import { UPDATE_LOCATION_FORM_KEY } from '@/shared/constants/form-keys';
import { useUpdateLocation } from '@/queries/locations';

export type UpdateLocationFormModel = UpdateLocationRequest;

interface UpdateLocationFormProps {
	open: boolean;
	onClose: () => void;
	location?: Location;
	level?: Level;
	formModel: UpdateLocationFormModel;
	onChangeFormModel: (field: keyof Location, value: string) => void;
	onSubmitted?: () => Promise<void> | void;
}

const UpdateLocationForm: FunctionComponent<UpdateLocationFormProps> = ({
	open,
	onClose,
	location,
	level,
	formModel,
	onChangeFormModel,
	onSubmitted,
}): ReactElement => {
	const { dispatch } = useToastMessage();

	const { isLoading, mutate } = useUpdateLocation({
		onSuccess: async () => {
			await onSubmitted?.();
			dispatch({
				type: 'success',
				content: `${capitalize(level?.name || '')} successfully updated`,
			});
			onClose();
		},
	});

	const disabled = useMemo(() => !formModel.name.length, [formModel]);

	if (!location || !level) {
		return <></>;
	}

	return (
		<FormDialog
			title="Edit location"
			open={open}
			onClose={onClose}
			actions={{
				confirm: { label: 'Update location', isLoading, disabled },
				cancel: { label: 'Cancel' },
			}}
			onSubmit={() =>
				mutate({
					id: location.id,
					request: { request: formModel, formKey: UPDATE_LOCATION_FORM_KEY },
				})
			}
			maxWidth="xs"
			fullWidth
		>
			<TextField
				formKey={UPDATE_LOCATION_FORM_KEY}
				name="name"
				label={`${capitalize(level.name)} name`}
				value={formModel.name}
				onChange={(event) => onChangeFormModel('name', event.target.value)}
				fullWidth
			/>
		</FormDialog>
	);
};

export default UpdateLocationForm;
